// @vendors
import { create } from 'zustand';

// @services
import postService from '../services/post.service';

// @types
import { CreatePostRequest, Post, PostResponse } from '../types/post.types';

interface PostStoreState {
  posts: Post[] | null;
  selectedPost: Post | null;
  isLoading: boolean;
  error: string | null;
  createPost: (data: CreatePostRequest) => Promise<any>;
  getPostsByProfessional: (professionalId: string, isActive?: boolean) => Promise<Post[]>;
  getPostById: (postId: string) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
  setSelectedPost: (post: Post | null) => void;
  getAllPosts: () => Promise<PostResponse>;
}

const usePostStore = create<PostStoreState>((set) => ({
  posts: [],
  selectedPost: null,
  isLoading: false,
  error: null,

  createPost: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await postService.createPost(data);
      set({ isLoading: false });
      return response;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error creating post',
        isLoading: false
      });
    }
  },

  getPostsByProfessional: async (professionalId: string, isActive?: boolean) => {
    set({ isLoading: true, error: null });
    try {
      const posts = await postService.getPostsByProfessional(professionalId, isActive);
      set({ posts, isLoading: false });
      return posts;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error fetching posts',
        isLoading: false
      });
      throw error;
    }
  },

  getPostById: async (postId) => {
    set({ isLoading: true, error: null });
    try {
      const post = await postService.getPostById(postId);
      set({ selectedPost: post, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error fetching post',
        isLoading: false
      });
    }
  },

  deletePost: async (postId) => {
    set({ isLoading: true, error: null });
    try {
      await postService.deletePost(postId);
      set((state) => ({
        posts: state.posts?.filter((post) => post.postId !== postId) || [],
        isLoading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error deleting post',
        isLoading: false
      });
    }
  },

  setSelectedPost: (post) => set({ selectedPost: post }),

  getAllPosts: async () => {
    set({ isLoading: true, error: null });
    try {
      const allPosts = await postService.getPostForAllCompanies();
      set({ isLoading: false });
      return allPosts;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
}));

export default usePostStore;